import { useRef, useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  CardBody,
  Label,
  Input,
  FormGroup,
  CardHeader,
} from "reactstrap";
import Loading from "../Loading/Loading";
import "./Reflection.scss";

// Import Advert component for Google ads
import Advert from "../Advert/Advert";

import CommonButton from "../CommonButton/CommonButton";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import GetAdminService from "../../services/admin/GetAdminService";
import CustomLoader from "../customLoader/CustomLoader";
import StudentGoalService from "../../services/StudentGoalService";
import PortFolioService from "../../services/PortfolioService";
import LaddersDataService from "../../services/LaddersService";

// Import button icons
import saveIcon from "../../assets/images/buttons/save.png";
import cancelIcon from "../../assets/images/buttons/cancel.png";

const IMG_URL = process.env.REACT_APP_IMAGE_ASSET_URL;

function Reflection() {
  let userData = useSelector((state) => state.UserReducer.userObj);
  const lang = useSelector((state) => state.LanguageReducer.current) || 'en';
  const isImpersonating = useSelector((state) => state.ImpersonationReducer?.isImpersonating);
  const isReadOnly = userData?.role === "parent" || isImpersonating;
  let history = useHistory();

  // Complete EN/ES labels for Reflection
  const labels = {
    en: {
      basicGradRequirements: 'Basic Graduation Requirements',
      basicGradSubtitle: 'Courses to consider to stay on track for graduation',
      ucCsuEligibility: 'UC/CSU Eligibility',
      ucCsuSubtitle: 'Courses that need consideration so you are ready to tackle a UC or CSU admission',
      firstClass: 'First class that needs remediation',
      secondClass: 'Second class that needs remediation',
      thirdClass: 'Third class that needs remediation',
      howToFix: 'How do you plan to fix this?',
      whenComplete: 'When do you complete this?',
      postHighSchoolPlans: 'Your current post-high school plans?',
      howDoingClasses: 'How are you doing in your classes?',
      howCanHelp: 'What can we do to help you?',
      helpSuccess: 'What can we do to help your success?',
      difficulties: 'Are you having any difficulties in your classes?',
      advisorHelp: 'How can your advisior better help you in your current challenges?',
      topThreeGoals: 'What are your immediate top three goals for this semester?',
      select: 'Select',
      takingMyself: 'I\'m taking the course by myself',
      gettingTutoring: 'I\'m getting tutoring',
      outsideProgram: 'I have enrolled in an outside program',
      needHelp: 'I need help taking the class',
      now: 'Now',
      nextSemester: 'Next Semester',
      summer: 'Summer',
      typeClassRemediation: 'Type your class remediation',
      typeHere: 'Type here',
      cancel: 'Cancel',
      save: 'Save',
      reflectionSaved: 'Reflection Saved',
      ok: 'OK',
      somethingWrong: 'Something went wrong',
      noUrlFound: 'No URL found in the response'
    },
    es: {
      basicGradRequirements: 'Requisitos Básicos de Graduación',
      basicGradSubtitle: 'Cursos a considerar para mantenerse en el camino hacia la graduación',
      ucCsuEligibility: 'Elegibilidad UC/CSU',
      ucCsuSubtitle: 'Cursos a considerar para que estés listo para una admisión a UC o CSU',
      firstClass: 'Clase que necesita recuperación',
      secondClass: 'Clase que necesita recuperación',
      thirdClass: 'Clase que necesita recuperación',
      howToFix: '¿Cómo planeas arreglar esto?',
      whenComplete: '¿Cuándo completarás esto?',
      postHighSchoolPlans: '¿Cuáles son tus planes actuales después de la preparatoria?',
      howDoingClasses: '¿Cómo te está yendo en tus clases?',
      howCanHelp: '¿Qué podemos hacer para ayudarte?',
      helpSuccess: '¿Qué podemos hacer para ayudarte a tener éxito?',
      difficulties: '¿Estás teniendo dificultades en tus clases?',
      advisorHelp: '¿Cómo puede tu consejero ayudarte mejor en tus desafíos actuales?',
      topThreeGoals: '¿Cuáles son tus tres metas principales inmediatas para este semestre?',
      select: 'Seleccionar',
      takingMyself: 'Estoy tomando el curso por mí mismo',
      gettingTutoring: 'Estoy recibiendo tutoría',
      outsideProgram: 'Me he inscrito en un programa externo',
      needHelp: 'Necesito ayuda para tomar la clase',
      now: 'Ahora',
      nextSemester: 'Próximo semestre',
      summer: 'Verano',
      typeClassRemediation: 'Ingresa la clase que necesita recuperación',
      typeHere: 'Escribe aquí',
      cancel: 'Cancelar',
      save: 'Guardar',
      reflectionSaved: 'Reflexión Guardada',
      ok: 'ACEPTAR',
      somethingWrong: 'Algo salió mal',
      noUrlFound: 'No se encontró URL en la respuesta'
    }
  };
  const t = (key) => (labels[lang] || labels.en)[key];

  // Normalize and map dropdown values across languages/synonyms
  const normalizeText = (str) =>
    (str || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\.,!]/g, "")
      .trim();

  const HOW_CODES = {
    TAKING_MYSELF: "TAKING_MYSELF",
    GETTING_TUTORING: "GETTING_TUTORING",
    OUTSIDE_PROGRAM: "OUTSIDE_PROGRAM",
    NEED_HELP: "NEED_HELP",
  };

  const HOW_PATTERNS = [
    {
      code: HOW_CODES.TAKING_MYSELF,
      patterns: [
        "taking the course by myself",
        "taking the course on my own",
        "tomando el curso por mi mismo",
        "tomando el curso por mi cuenta",
        "tomando el curso solo",
      ],
    },
    {
      code: HOW_CODES.GETTING_TUTORING,
      patterns: [
        "getting tutoring",
        "receiving tutoring",
        "recibiendo tutoria",
        "recibiendo tutoria",
        "estoy recibiendo tutoria",
      ],
    },
    {
      code: HOW_CODES.OUTSIDE_PROGRAM,
      patterns: [
        "enrolled in an outside program",
        "enrolled in an external program",
        "inscrito en un programa externo",
        "me he inscrito en un programa externo",
      ],
    },
    {
      code: HOW_CODES.NEED_HELP,
      patterns: [
        "need help taking the class",
        "necesito ayuda para tomar la clase",
      ],
    },
  ];

  const normalizeHowValue = (str) => {
    const s = normalizeText(str);
    for (const entry of HOW_PATTERNS) {
      if (entry.patterns.some((p) => s.includes(p))) return entry.code;
    }
    return "Select";
  };

  const labelForHow = (code) => {
    switch (code) {
      case HOW_CODES.TAKING_MYSELF:
        return t("takingMyself");
      case HOW_CODES.GETTING_TUTORING:
        return t("gettingTutoring");
      case HOW_CODES.OUTSIDE_PROGRAM:
        return t("outsideProgram");
      case HOW_CODES.NEED_HELP:
        return t("needHelp");
      default:
        return "";
    }
  };

  const WHEN_CODES = {
    NOW: "NOW",
    NEXT_SEMESTER: "NEXT_SEMESTER",
    SUMMER: "SUMMER",
  };

  const WHEN_PATTERNS = [
    { code: WHEN_CODES.NOW, patterns: ["now", "ahora"] },
    {
      code: WHEN_CODES.NEXT_SEMESTER,
      patterns: ["next semester", "proximo semestre", "Próximo semestre"],
    },
    { code: WHEN_CODES.SUMMER, patterns: ["summer", "verano"] },
  ];

  const normalizeWhenValue = (str) => {
    const s = normalizeText(str);
    for (const entry of WHEN_PATTERNS) {
      if (entry.patterns.some((p) => s.includes(p))) return entry.code;
    }
    return "Select";
  };

  const labelForWhen = (code) => {
    switch (code) {
      case WHEN_CODES.NOW:
        return t("now");
      case WHEN_CODES.NEXT_SEMESTER:
        return t("nextSemester");
      case WHEN_CODES.SUMMER:
        return t("summer");
      default:
        return "";
    }
  };

  const [className1, setClassName1] = useState("");
  const [className2, setClassName2] = useState("");
  const [className3, setClassName3] = useState("");
  const [className4, setClassName4] = useState("");
  const [className5, setClassName5] = useState("");
  const [className6, setClassName6] = useState("");
  const [activeInput, setActiveInput] = useState(null);

  const [showSchoolNameContainer, setShowSchoolNameContainer] = useState(false);
  const [schoolNameList, setSchoolNameList] = useState([]);

  const firstClassRef = useRef();
  const secondClassRef = useRef();
  const thirdClassRef = useRef();
  const fourthClassRef = useRef();
  const fifthClassRef = useRef();
  const sixthClassRef = useRef();
  const timeoutRef = useRef();
  const [schoolNameLoading, setSchoolNameLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schoolId, setSchoolId] = useState(null);
  const [studentListData, setStudentListData] = useState([]);

  const [selectedOptionHow1, setSelectedOptionHow1] = useState("Select");
  const [selectedOptionHow2, setSelectedOptionHow2] = useState("Select");
  const [selectedOptionHow3, setSelectedOptionHow3] = useState("Select");
  const [selectedOptionHow4, setSelectedOptionHow4] = useState("Select");
  const [selectedOptionHow5, setSelectedOptionHow5] = useState("Select");
  const [selectedOptionHow6, setSelectedOptionHow6] = useState("Select");

  const [selectedOptionWhen1, setSelectedOptionWhen1] = useState("Select");
  const [selectedOptionWhen2, setSelectedOptionWhen2] = useState("Select");
  const [selectedOptionWhen3, setSelectedOptionWhen3] = useState("Select");
  const [selectedOptionWhen4, setSelectedOptionWhen4] = useState("Select");
  const [selectedOptionWhen5, setSelectedOptionWhen5] = useState("Select");
  const [selectedOptionWhen6, setSelectedOptionWhen6] = useState("Select");

  const [highSchool, sethighSchool] = useState("");
  const [doing, setDoing] = useState("");
  const [help, setHelp] = useState("");
  const [success, setSuccess] = useState("");
  const [difficulties, setDifficulties] = useState("");
  const [challenges, setChallenges] = useState("");
  const [threeGoals, setThreeGoals] = useState("");
  const [studentId, setStudentId] = useState();
  const [studentList, setStudentList] = useState([]);

  const getStudentList = () => {
    setLoading(true);
    LaddersDataService.getParentRoleStudentList({ language: lang })
      .then((res) => {
        if (res?.status === 200 && res?.data?.list?.length > 0) {
          let studentArr = res.data.list || [];
          setStudentList(studentArr);
          if (studentArr.length > 0) {
            setStudentId(studentArr[0].id);
          }
          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // Call getStudentList when language changes or on initial load
    getStudentList();
  }, [lang]);

  useEffect(() => {
    // Call getReflection for student role when language changes
    if (userData?.role === "student") {
      getReflection();
    }
    // Call getReflection for parent role when language changes and studentId exists
    else if (userData?.role === "parent" && studentId) {
      getReflection();
    }
  }, [lang, studentId]);

  const getClassesList = (goal_id, school_id, value) => {
    setSchoolNameList([]);
    setShowSchoolNameContainer(true);
    setSchoolNameLoading(true);
    GetAdminService.getClassesList(goal_id, school_id, value, lang)
      .then((data) => {
        setSchoolNameList(data?.data?.class_names || []);
        setSchoolNameLoading(false);
      })
      .catch((error) => {
        setSchoolNameLoading(false);
      });
  };

  const handleSchoolName = (e, index) => {
    clearTimeout(timeoutRef.current);
    const value = e.target.value;

    setActiveInput(index);
    switch (index) {
      case 1:
        setClassName1(value);
        break;
      case 2:
        setClassName2(value);
        break;
      case 3:
        setClassName3(value);
        break;
      case 4:
        setClassName4(value);
        break;
      case 5:
        setClassName5(value);
        break;
      case 6:
        setClassName6(value);
        break;
      default:
        break;
    }

    if (!value) {
      setShowSchoolNameContainer(false);
      setSchoolNameList([]);
      setStudentListData([]);
    } else {
      timeoutRef.current = setTimeout(() => {
        const goal_id = index <= 3 ? "75" : "45";
        getClassesList(goal_id, userData?.school_id, value);
      }, 500);
    }
  };

  const handleSelectedSchoolName = (item, index) => {
    switch (activeInput) {
      case 1:
        setClassName1(item.name);
        break;
      case 2:
        setClassName2(item.name);
        break;
      case 3:
        setClassName3(item.name);
        break;
      case 4:
        setClassName4(item.name);
        break;
      case 5:
        setClassName5(item.name);
        break;
      case 6:
        setClassName6(item.name);
        break;
      default:
        break;
    }

    setSchoolId(item?.id);
    setShowSchoolNameContainer(false);
    setSchoolNameList([item]);
  };

  const handleImageClick = () => {
    let obj = {
      user_id: userData?.role === "student" ? userData?.id : studentId,
    };

    setLoading(true);
    StudentGoalService.getPdfReflection(obj)
      .then((data) => {
        if (data?.data?.url) {
          window.open(data.data.url, "_blank");
        } else {
          swal("", t('noUrlFound'), "warning");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        const errorMessage =
          error?.response?.data?.error || t('somethingWrong');
        swal("", errorMessage, "error");
      });
  };

  const getReflection = () => {
    setLoading(true);
    let obj = {
      student_id: studentId,
      language: lang,
    };
    const reflectionService =
      userData.role === "student"
        ? PortFolioService.getReflection({ language: lang })
        : PortFolioService.getParentReflection(obj);
    reflectionService
      .then((data) => {
        const reflections = data?.data?.reflections || [];
        if (reflections?.basic_class_1) {
          setClassName1(reflections?.basic_class_1);
        } else {
          setClassName1("");
        }
        if (reflections?.basic_class_2) {
          setClassName2(reflections?.basic_class_2);
        } else {
          setClassName2("");
        }
        if (reflections?.basic_class_3) {
          setClassName3(reflections?.basic_class_3);
        } else {
          setClassName3("");
        }
        if (reflections?.uc_class_1) {
          setClassName4(reflections?.uc_class_1);
        } else {
          setClassName4("");
        }
        if (reflections?.uc_class_2) {
          setClassName5(reflections?.uc_class_2);
        } else {
          setClassName5("");
        }
        if (reflections?.uc_class_3) {
          setClassName6(reflections?.uc_class_3);
        } else {
          setClassName6("");
        }
        setSelectedOptionHow1(normalizeHowValue(reflections?.basic_how_1));
        setSelectedOptionHow2(normalizeHowValue(reflections?.basic_how_2));
        setSelectedOptionHow3(normalizeHowValue(reflections?.basic_how_3));
        setSelectedOptionHow4(normalizeHowValue(reflections?.uc_how_1));
        setSelectedOptionHow5(normalizeHowValue(reflections?.uc_how_2));
        setSelectedOptionHow6(normalizeHowValue(reflections?.uc_how_3));

        setSelectedOptionWhen1(normalizeWhenValue(reflections?.basic_when_1));
        setSelectedOptionWhen2(normalizeWhenValue(reflections?.basic_when_2));
        setSelectedOptionWhen3(normalizeWhenValue(reflections?.basic_when_3));
        setSelectedOptionWhen4(normalizeWhenValue(reflections?.uc_when_1));
        setSelectedOptionWhen5(normalizeWhenValue(reflections?.uc_when_2));
        setSelectedOptionWhen6(normalizeWhenValue(reflections?.uc_when_3));
        if (reflections?.post_high_school_plans) {
          sethighSchool(reflections?.post_high_school_plans);
        } else {
          sethighSchool("");
        }
        if (reflections?.class_performance) {
          setDoing(reflections?.class_performance);
        } else {
          setDoing("");
        }
        if (reflections?.support_from_advisor) {
          setHelp(reflections?.support_from_advisor);
        } else {
          setHelp("");
        }
        if (reflections?.support_from_student) {
          setSuccess(reflections?.support_from_student);
        } else {
          setSuccess("");
        }
        if (reflections?.difficulties_in_classes) {
          setDifficulties(reflections?.difficulties_in_classes);
        } else {
          setDifficulties("");
        }
        if (reflections?.advisor_support) {
          setChallenges(reflections?.advisor_support);
        } else {
          setChallenges("");
        }
        if (reflections?.top_three_goals) {
          setThreeGoals(reflections?.top_three_goals);
        } else {
          setThreeGoals("");
        }

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleReflectionSubmit = () => {
    let obj = {
      reflection: {
        user_id: userData?.id,
        language: lang,
        basic_class_1: className1 || "",
        basic_how_1:
          selectedOptionHow1 !== "Select" ? labelForHow(selectedOptionHow1) : "",
        basic_when_1:
          selectedOptionWhen1 !== "Select" ? labelForWhen(selectedOptionWhen1) : "",
        basic_class_2: className2 || "",
        basic_how_2:
          selectedOptionHow2 !== "Select" ? labelForHow(selectedOptionHow2) : "",
        basic_when_2:
          selectedOptionWhen2 !== "Select" ? labelForWhen(selectedOptionWhen2) : "",
        basic_class_3: className3 || "",
        basic_how_3:
          selectedOptionHow3 !== "Select" ? labelForHow(selectedOptionHow3) : "",
        basic_when_3:
          selectedOptionWhen3 !== "Select" ? labelForWhen(selectedOptionWhen3) : "",
        uc_class_1: className4 || "",
        uc_how_1:
          selectedOptionHow4 !== "Select" ? labelForHow(selectedOptionHow4) : "",
        uc_when_1:
          selectedOptionWhen4 !== "Select" ? labelForWhen(selectedOptionWhen4) : "",
        uc_class_2: className5 || "",
        uc_how_2:
          selectedOptionHow5 !== "Select" ? labelForHow(selectedOptionHow5) : "",
        uc_when_2:
          selectedOptionWhen5 !== "Select" ? labelForWhen(selectedOptionWhen5) : "",
        uc_class_3: className6 || "",
        uc_how_3:
          selectedOptionHow6 !== "Select" ? labelForHow(selectedOptionHow6) : "",
        uc_when_3:
          selectedOptionWhen6 !== "Select" ? labelForWhen(selectedOptionWhen6) : "",
        post_high_school_plans: highSchool || "",
        class_performance: doing || "",
        support_from_advisor: help || "",
        support_from_student: success || "",
        difficulties_in_classes: difficulties || "",
        advisor_support: challenges || "",
        top_three_goals: threeGoals || "",
      },
    };
    setLoading(true);
    StudentGoalService.saveReflection(obj)
      .then((data) => {
        setLoading(false);
        if (data?.data?.success) {
          swal({
            text: t('reflectionSaved'),
            icon: "success",
            buttons: {
              [t('ok')]: true,
            },
            closeOnClickOutside: false,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        const errorMessage =
          error?.response?.data?.error || t('somethingWrong');
        swal("", errorMessage, "error");
      });
  };

  const handleChangeStudent = (e) => {
    setStudentId(e.target.value);
  };

  return (
    <div className={`reflection-page ${isImpersonating ? 'impersonation-readonly' : ''}`} style={{paddingTop:'20px'}}>
      <div className="reflection-header">
        <div className="header-content">
          <div className="ad-section-centered">
            <Advert />
          </div>
        </div>
      </div>

      <Row>
        <Col lg="12" />
      </Row>

      {/* Basic Graduation Requirements Section */}
      <div className="reflection-container">
        <Row>
          <Col lg="12" className="mx-auto">
            <Card className="main-card mb-3 mt-3 interest-main">
              <div className="d-flex justify-content-between align-items-center mb-3">
                {/* <h3 className="checklist-title"></h3> */}
                <Col lg="6">
                  {userData.role === "parent" &&
                  studentList &&
                  studentList.length > 0 ? (
                    <Row>
                      <Col lg="8">
                        <FormGroup className="text-left">
                          <Input
                            type="select"
                            name="studentId"
                            value={studentId || ""}
                            onChange={handleChangeStudent}
                            style={{ backgroundColor: "white" }}
                          >
                            {studentList.map((item, index) => (
                              <option key={index} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  ) : (
                    ""
                  )}
                </Col>
                <button
                  onClick={handleImageClick}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 20px",
                    backgroundColor: "green",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={IMG_URL + "pdf.png"}
                    alt="Download"
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "10px",
                    }}
                  />
                  PDF
                </button>
              </div>
              <CardBody>
                {loading ? (
                  <Loading />
                ) : (
                  <>
                    <Row>
                      <Col lg="12">
                        <Label style={{ fontWeight: "bold", fontSize: "18px" }}>
                          {t('basicGradRequirements')}
                        </Label>
                      </Col>
                      <Col lg="12">
                        <p style={{ color: "#6c757d", fontSize: "14px", marginTop: "4px", marginBottom: "12px" }}>
                          {t('basicGradSubtitle')}
                        </p>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('firstClass')}
                          </Label>
                          <div>
                            <Input
                              ref={firstClassRef}
                              className="search-student-school-input"
                              type="text"
                              placeholder={t('typeClassRemediation')}
                              value={className1}
                              onChange={(e) => handleSchoolName(e, 1)}
                              autoComplete="off"
                              disabled={userData?.role === "parent"}
                            />
                            {activeInput === 1 &&
                              showSchoolNameContainer &&
                              schoolNameList.length > 0 && (
                                <ul
                                  className="maintain-group-student-dropdown-menu"
                                  style={{
                                    width: "100%",
                                    maxWidth: "740px",
                                    maxHeight: "320px",
                                    overflowY: "scroll",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {schoolNameLoading && <CustomLoader />}
                                  {schoolNameList.map((item, index) => (
                                    <li
                                      onClick={() =>
                                        handleSelectedSchoolName(item, 1)
                                      }
                                      key={index}
                                      className="flex admin-school-list cursor"
                                    >
                                      <span>{item.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howToFix')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionHow1}
                            onChange={(e) =>
                              setSelectedOptionHow1(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="TAKING_MYSELF">
                              {t('takingMyself')}
                            </option>
                            <option value="GETTING_TUTORING">
                              {t('gettingTutoring')}
                            </option>
                            <option value="OUTSIDE_PROGRAM">
                              {t('outsideProgram')}
                            </option>
                            <option value="NEED_HELP">
                              {t('needHelp')}
                            </option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('whenComplete')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionWhen1}
                            onChange={(e) =>
                              setSelectedOptionWhen1(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="NOW">{t('now')}</option>
                            <option value="NEXT_SEMESTER">{t('nextSemester')}</option>
                            <option value="SUMMER">{t('summer')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('secondClass')}
                          </Label>
                          <div>
                            <Input
                              ref={secondClassRef}
                              className="search-student-school-input"
                              type="text"
                              placeholder={t('typeClassRemediation')}
                              value={className2}
                              onChange={(e) => handleSchoolName(e, 2)}
                              autoComplete="off"
                              disabled={userData?.role === "parent"}
                            />
                            {activeInput === 2 &&
                              showSchoolNameContainer &&
                              schoolNameList.length > 0 && (
                                <ul
                                  className="maintain-group-student-dropdown-menu"
                                  style={{
                                    width: "100%",
                                    maxWidth: "740px",
                                    maxHeight: "320px",
                                    overflowY: "scroll",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {schoolNameLoading && <CustomLoader />}
                                  {schoolNameList.map((item, index) => (
                                    <li
                                      onClick={() =>
                                        handleSelectedSchoolName(item, 2)
                                      }
                                      key={index}
                                      className="flex admin-school-list cursor"
                                    >
                                      <span>{item.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howToFix')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionHow2}
                            onChange={(e) =>
                              setSelectedOptionHow2(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="TAKING_MYSELF">
                              {t('takingMyself')}
                            </option>
                            <option value="GETTING_TUTORING">
                              {t('gettingTutoring')}
                            </option>
                            <option value="OUTSIDE_PROGRAM">
                              {t('outsideProgram')}
                            </option>
                            <option value="NEED_HELP">
                              {t('needHelp')}
                            </option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('whenComplete')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionWhen2}
                            onChange={(e) =>
                              setSelectedOptionWhen2(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="NOW">{t('now')}</option>
                            <option value="NEXT_SEMESTER">{t('nextSemester')}</option>
                            <option value="SUMMER">{t('summer')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('thirdClass')}
                          </Label>
                          <div>
                            <Input
                              ref={thirdClassRef}
                              className="search-student-school-input"
                              type="text"
                              placeholder={t('typeClassRemediation')}
                              value={className3}
                              onChange={(e) => handleSchoolName(e, 3)}
                              autoComplete="off"
                              disabled={userData?.role === "parent"}
                            />
                            {activeInput === 3 &&
                              showSchoolNameContainer &&
                              schoolNameList.length > 0 && (
                                <ul
                                  className="maintain-group-student-dropdown-menu"
                                  style={{
                                    width: "100%",
                                    maxWidth: "740px",
                                    maxHeight: "320px",
                                    overflowY: "scroll",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {schoolNameLoading && <CustomLoader />}
                                  {schoolNameList.map((item, index) => (
                                    <li
                                      onClick={() =>
                                        handleSelectedSchoolName(item, 3)
                                      }
                                      key={index}
                                      className="flex admin-school-list cursor"
                                    >
                                      <span>{item.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howToFix')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionHow3}
                            onChange={(e) =>
                              setSelectedOptionHow3(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="TAKING_MYSELF">
                              {t('takingMyself')}
                            </option>
                            <option value="GETTING_TUTORING">
                              {t('gettingTutoring')}
                            </option>
                            <option value="OUTSIDE_PROGRAM">
                              {t('outsideProgram')}
                            </option>
                            <option value="NEED_HELP">
                              {t('needHelp')}
                            </option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('whenComplete')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionWhen3}
                            onChange={(e) =>
                              setSelectedOptionWhen3(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="NOW">{t('now')}</option>
                            <option value="NEXT_SEMESTER">{t('nextSemester')}</option>
                            <option value="SUMMER">{t('summer')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* UC/CSU Eligibility Section */}
      <div className="reflection-container">
        <Row>
          <Col lg="12" className="mx-auto">
            <Card className="main-card mb-3 mt-3 interest-main">
              <CardBody>
                {loading ? (
                  <Loading />
                ) : (
                  <>
                    <Row>
                      <Col lg="12">
                        <Label style={{ fontWeight: "bold", fontSize: "18px" }}>
                          {t('ucCsuEligibility')}
                        </Label>
                      </Col>
                      <Col lg="12">
                        <p style={{ color: "#6c757d", fontSize: "14px", marginTop: "4px", marginBottom: "12px" }}>
                          {t('ucCsuSubtitle')}
                        </p>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('firstClass')}
                          </Label>
                          <div>
                            <Input
                              ref={fourthClassRef}
                              className="search-student-school-input"
                              type="text"
                              placeholder={t('typeClassRemediation')}
                              value={className4}
                              onChange={(e) => handleSchoolName(e, 4)}
                              autoComplete="off"
                              disabled={userData?.role === "parent"}
                            />
                            {activeInput === 4 &&
                              showSchoolNameContainer &&
                              schoolNameList.length > 0 && (
                                <ul
                                  className="maintain-group-student-dropdown-menu"
                                  style={{
                                    width: "100%",
                                    maxWidth: "740px",
                                    maxHeight: "320px",
                                    overflowY: "scroll",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {schoolNameLoading && <CustomLoader />}
                                  {schoolNameList.map((item, index) => (
                                    <li
                                      onClick={() =>
                                        handleSelectedSchoolName(item, 4)
                                      }
                                      key={index}
                                      className="flex admin-school-list cursor"
                                    >
                                      <span>{item.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howToFix')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionHow4}
                            onChange={(e) =>
                              setSelectedOptionHow4(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="TAKING_MYSELF">
                              {t('takingMyself')}
                            </option>
                            <option value="GETTING_TUTORING">
                              {t('gettingTutoring')}
                            </option>
                            <option value="OUTSIDE_PROGRAM">
                              {t('outsideProgram')}
                            </option>
                            <option value="NEED_HELP">
                              {t('needHelp')}
                            </option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('whenComplete')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionWhen4}
                            onChange={(e) =>
                              setSelectedOptionWhen4(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="NOW">{t('now')}</option>
                            <option value="NEXT_SEMESTER">{t('nextSemester')}</option>
                            <option value="SUMMER">{t('summer')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('secondClass')}
                          </Label>
                          <div>
                            <Input
                              ref={fifthClassRef}
                              className="search-student-school-input"
                              type="text"
                              placeholder={t('typeClassRemediation')}
                              value={className5}
                              onChange={(e) => handleSchoolName(e, 5)}
                              autoComplete="off"
                              disabled={userData?.role === "parent"}
                            />
                            {activeInput === 5 &&
                              showSchoolNameContainer &&
                              schoolNameList.length > 0 && (
                                <ul
                                  className="maintain-group-student-dropdown-menu"
                                  style={{
                                    width: "100%",
                                    maxWidth: "740px",
                                    maxHeight: "320px",
                                    overflowY: "scroll",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {schoolNameLoading && <CustomLoader />}
                                  {schoolNameList.map((item, index) => (
                                    <li
                                      onClick={() =>
                                        handleSelectedSchoolName(item, 5)
                                      }
                                      key={index}
                                      className="flex admin-school-list cursor"
                                    >
                                      <span>{item.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howToFix')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionHow5}
                            onChange={(e) =>
                              setSelectedOptionHow5(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="TAKING_MYSELF">
                              {t('takingMyself')}
                            </option>
                            <option value="GETTING_TUTORING">
                              {t('gettingTutoring')}
                            </option>
                            <option value="OUTSIDE_PROGRAM">
                              {t('outsideProgram')}
                            </option>
                            <option value="NEED_HELP">
                              {t('needHelp')}
                            </option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('whenComplete')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionWhen5}
                            onChange={(e) =>
                              setSelectedOptionWhen5(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="NOW">{t('now')}</option>
                            <option value="NEXT_SEMESTER">{t('nextSemester')}</option>
                            <option value="SUMMER">{t('summer')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('thirdClass')}
                          </Label>
                          <div>
                            <Input
                              ref={sixthClassRef}
                              className="search-student-school-input"
                              type="text"
                              placeholder={t('typeClassRemediation')}
                              value={className6}
                              onChange={(e) => handleSchoolName(e, 6)}
                              autoComplete="off"
                              disabled={userData?.role === "parent"}
                            />
                            {activeInput === 6 &&
                              showSchoolNameContainer &&
                              schoolNameList.length > 0 && (
                                <ul
                                  className="maintain-group-student-dropdown-menu"
                                  style={{
                                    width: "100%",
                                    maxWidth: "740px",
                                    maxHeight: "320px",
                                    overflowY: "scroll",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  {schoolNameLoading && <CustomLoader />}
                                  {schoolNameList.map((item, index) => (
                                    <li
                                      onClick={() =>
                                        handleSelectedSchoolName(item, 6)
                                      }
                                      key={index}
                                      className="flex admin-school-list cursor"
                                    >
                                      <span>{item.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howToFix')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionHow6}
                            onChange={(e) =>
                              setSelectedOptionHow6(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="TAKING_MYSELF">{t('takingMyself')}</option>
                            <option value="GETTING_TUTORING">{t('gettingTutoring')}</option>
                            <option value="OUTSIDE_PROGRAM">{t('outsideProgram')}</option>
                            <option value="NEED_HELP">{t('needHelp')}</option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('whenComplete')}
                          </Label>
                          <Input
                            type="select"
                            className="custom-select"
                            value={selectedOptionWhen6}
                            onChange={(e) =>
                              setSelectedOptionWhen6(e.target.value)
                            }
                            disabled={userData?.role === "parent"}
                          >
                            <option value="Select">{t('select')}</option>
                            <option value="NOW">{t('now')}</option>
                            <option value="NEXT_SEMESTER">{t('nextSemester')}</option>
                            <option value="SUMMER">{t('summer')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('postHighSchoolPlans')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={highSchool}
                            onChange={(e) => sethighSchool(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howDoingClasses')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={doing}
                            onChange={(e) => setDoing(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('howCanHelp')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={help}
                            onChange={(e) => setHelp(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('helpSuccess')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={success}
                            onChange={(e) => setSuccess(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('difficulties')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={difficulties}
                            onChange={(e) => setDifficulties(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('advisorHelp')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={challenges}
                            onChange={(e) => setChallenges(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup className="position-relative">
                          <Label className="labelBold">
                            {t('topThreeGoals')}
                          </Label>
                          <Input
                            type="textarea"
                            className="custom-textarea"
                            value={threeGoals}
                            onChange={(e) => setThreeGoals(e.target.value)}
                            rows="2"
                            placeholder={t('typeHere')}
                            disabled={userData?.role === "parent"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Action Buttons */}
      {userData?.role === "student" && !isImpersonating && (
        <div className="reflection-actions">
          <button
            className="action-btn cancel-btn"
            onClick={() => history.goBack()}
          >
            <img src={cancelIcon} alt={t('cancel')} className="btn-icon" />
            {t('cancel')}
          </button>
          <button
            className="action-btn save-btn"
            onClick={() => handleReflectionSubmit()}
            disabled={
              !(
                (className1 &&
                  selectedOptionHow1 !== "Select" &&
                  selectedOptionWhen1 !== "Select") ||
                (className2 &&
                  selectedOptionHow2 !== "Select" &&
                  selectedOptionWhen2 !== "Select") ||
                (className3 &&
                  selectedOptionHow3 !== "Select" &&
                  selectedOptionWhen3 !== "Select") ||
                (className4 &&
                  selectedOptionHow4 !== "Select" &&
                  selectedOptionWhen4 !== "Select") ||
                (className5 &&
                  selectedOptionHow5 !== "Select" &&
                  selectedOptionWhen5 !== "Select") ||
                (className6 &&
                  selectedOptionHow6 !== "Select" &&
                  selectedOptionWhen6 !== "Select") ||
                highSchool.length > 0 ||
                doing.length > 0 ||
                help.length > 0 ||
                success.length > 0 ||
                difficulties.length > 0 ||
                challenges.length > 0 ||
                threeGoals.length > 0
              )
            }
          >
            <img src={saveIcon} alt={t('save')} className="btn-icon" />
            {t('save')}
          </button>
        </div>
      )}
    </div>
  );
}

export default Reflection;
